import CustomButton from "components/common/Button/CustomButton";
import DateRangePickerNew from "components/common/DateRangePicker/DateRangePickerNew";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  AiOutlineCheckCircle,
  AiOutlineDelete,
  AiOutlineDown,
  AiOutlinePlus,
  AiOutlineSave,
} from "react-icons/ai";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiClipboard,
  FiFileText,
  FiSearch,
  FiUsers,
} from "react-icons/fi";
import { HiOutlineClipboardCheck } from "react-icons/hi";
import { classNames } from "Utils/utils";
import { INVESTIGATION_CHECKS, REMEDIAL_ACTIONS } from "../constants";
import useCareLabels from "hooks/useCareLabels";

/**
 * CollapsibleSection - Reusable accordion section component
 */
const CollapsibleSection = ({
  title,
  icon,
  children,
  isExpanded,
  onToggle,
  className = "",
  required = false,
  headerClassName = "",
  badgeText = "",
  allowOverflow = false,
}) => {
  return (
    <div
      className={classNames(
        "border border-gray-200 rounded-xl",
        isExpanded && allowOverflow ? "overflow-visible" : "overflow-hidden",
        className,
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className={classNames(
          "w-full flex items-center justify-between px-5 py-4 bg-gray-50 hover:bg-gray-100 transition-colors",
          headerClassName,
        )}
      >
        <div className="flex items-center gap-3">
          {icon && <span className="text-gray-600">{icon}</span>}
          <span className="text-sm font-semibold text-gray-900">
            {title}
            {required && <span className="text-red-500 ml-1">*</span>}
          </span>
          {badgeText && (
            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
              {badgeText}
            </span>
          )}
        </div>
        <AiOutlineDown
          className={classNames(
            "w-5 h-5 text-gray-500 transition-transform duration-200",
            isExpanded ? "rotate-180" : "rotate-0",
          )}
        />
      </button>
      <div
        className={classNames(
          "transition-all duration-200",
          isExpanded
            ? `max-h-[2000px] opacity-100 ${allowOverflow ? "overflow-visible" : "overflow-hidden"}`
            : "max-h-0 opacity-0 overflow-hidden",
        )}
      >
        <div className="p-5 border-t border-gray-200">{children}</div>
      </div>
    </div>
  );
};

/**
 * Yes/No/NA Toggle - Reusable for investigation checks
 * Supports readOnly for view mode (investigation view)
 */
const YesNoNAToggle = ({ value, onChange, name, readOnly = false }) => {
  const options = [
    { val: "yes", label: "Yes", color: "bg-green-500" },
    { val: "no", label: "No", color: "bg-red-500" },
    { val: "na", label: "N/A", color: "bg-gray-500" },
  ];

  return (
    <div
      className={classNames(
        "inline-flex rounded-lg border border-gray-200 p-0.5",
        readOnly ? "bg-gray-100 cursor-default" : "bg-gray-50",
      )}
    >
      {options.map((opt) => (
        <button
          key={opt.val}
          type="button"
          onClick={() => !readOnly && onChange?.(opt.val)}
          disabled={readOnly}
          className={classNames(
            "px-3 py-1.5 text-xs font-medium rounded-md transition-all",
            value === opt.val
              ? `${opt.color} text-white`
              : "text-gray-600 hover:bg-gray-100",
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
};

/**
 * InvestigationForm Component (Part 4)
 * Used by managers to conduct NDIS incident investigation
 *
 * Fields stored in investigation_data JSON:
 * - is_reportable (yes/no)
 * - participant_interviews (array of {notes})
 * - staff_interviews (array of {notes})
 * - advocacy_offered (yes/no)
 * - advocacy_contact_name (when yes)
 * - advocacy_details (when no)
 * - 17 Yes/No/NA investigation checks
 * - root_cause_analysis
 * - remedial_actions (array)
 * - remedial_actions_other
 */
export const InvestigationForm = ({
  incident,
  onSubmit,
  onSaveDraft,
  onCancel,
  isLoading = false,
  initialData = null,
  readOnly = false,
}) => {
  const { participantLabel } = useCareLabels();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: initialData || {
      is_reportable: "",
      advocacy_offered: "",
      advocacy_contact_name: "",
      advocacy_details: "",
      root_cause_analysis: "",
      // Investigation checks (from INVESTIGATION_CHECKS constant)
      site_visit_conducted: "",
      routine_activities: "",
      equipment_contributed: "",
      equipment_designed_for_task: "",
      equipment_maintained: "",
      equipment_failed: "",
      risk_assessment_done: "",
      safety_instructions_provided: "",
      swp_documented: "",
      swp_followed: "",
      ppe_used: "",
      person_trained: "",
      behavior_issue_contributed: "",
      behavior_plan_exists: "",
      behavior_plan_followed: "",
      housekeeping_contributed: "",
      work_environment_contributed: "",
      remedial_actions: [],
      remedial_actions_other: "",
    },
  });

  // Watch values for conditional fields
  const selectedRemedialActions = watch("remedial_actions") || [];
  const advocacyOffered = watch("advocacy_offered");
  const hasRemedialOthers = selectedRemedialActions.includes("others");

  // Clear dependent fields when parent option changes (same pattern as other components)
  useEffect(() => {
    if (advocacyOffered !== "yes") {
      setValue("advocacy_contact_name", "");
      clearErrors("advocacy_contact_name");
    }
    if (advocacyOffered !== "no") {
      setValue("advocacy_details", "");
      clearErrors("advocacy_details");
    }
  }, [advocacyOffered, setValue, clearErrors]);

  useEffect(() => {
    if (!hasRemedialOthers) {
      setValue("remedial_actions_other", "");
      clearErrors("remedial_actions_other");
    }
  }, [hasRemedialOthers, setValue, clearErrors]);

  // State for interview notes arrays
  const [participantInterviews, setParticipantInterviews] = useState(
    initialData?.participant_interviews || [{ notes: "" }],
  );
  const [staffInterviews, setStaffInterviews] = useState(
    initialData?.staff_interviews || [{ notes: "" }],
  );

  // State for collapsible sections - first section expanded by default
  const [expandedSections, setExpandedSections] = useState({
    reportable: true,
    interviews: false,
    advocacy: false,
    checks: false,
    rootCause: false,
    remedial: false,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const expandAllSections = () => {
    setExpandedSections({
      reportable: true,
      interviews: true,
      advocacy: true,
      checks: true,
      rootCause: true,
      remedial: true,
    });
  };

  const collapseAllSections = () => {
    setExpandedSections({
      reportable: false,
      interviews: false,
      advocacy: false,
      checks: false,
      rootCause: false,
      remedial: false,
    });
  };

  const handleFormSubmit = (data) => {
    onSubmit({
      ...data,
      participant_interviews: participantInterviews.filter((i) =>
        i.notes.trim(),
      ),
      staff_interviews: staffInterviews.filter((i) => i.notes.trim()),
    });
  };

  const handleDraftSave = () => {
    const data = watch();
    onSaveDraft?.({
      ...data,
      participant_interviews: participantInterviews.filter((i) =>
        i.notes.trim(),
      ),
      staff_interviews: staffInterviews.filter((i) => i.notes.trim()),
    });
  };

  // Max interview limit
  const MAX_INTERVIEWS = 10;

  const addParticipantInterview = () => {
    if (participantInterviews.length < MAX_INTERVIEWS) {
      setParticipantInterviews([...participantInterviews, { notes: "" }]);
    }
  };

  const removeParticipantInterview = (index) => {
    if (participantInterviews.length > 1) {
      setParticipantInterviews(
        participantInterviews.filter((_, i) => i !== index),
      );
    }
  };

  const updateParticipantInterview = (index, value) => {
    const updated = [...participantInterviews];
    updated[index] = { notes: value };
    setParticipantInterviews(updated);
  };

  const addStaffInterview = () => {
    if (staffInterviews.length < MAX_INTERVIEWS) {
      setStaffInterviews([...staffInterviews, { notes: "" }]);
    }
  };

  const removeStaffInterview = (index) => {
    if (staffInterviews.length > 1) {
      setStaffInterviews(staffInterviews.filter((_, i) => i !== index));
    }
  };

  const updateStaffInterview = (index, value) => {
    const updated = [...staffInterviews];
    updated[index] = { notes: value };
    setStaffInterviews(updated);
  };

  const formContainerRef = useRef(null);

  const scrollToTopOnError = () => {
    requestAnimationFrame(() => {
      let el = formContainerRef.current;
      while (el) {
        const style = getComputedStyle(el);
        if (
          el.scrollHeight > el.clientHeight &&
          ["auto", "scroll"].includes(style.overflowY)
        ) {
          el.scrollTo({ top: 0, behavior: "smooth" });
          break;
        }
        el = el.parentElement;
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  return (
    <div ref={formContainerRef} className="p-6">
      {/* Subheader with expand/collapse controls */}
      <div className="mb-6 pb-4 border-b border-gray-200 flex items-center justify-between">
        <p className="text-gray-600 text-sm">
          Complete the investigation for Incident{" "}
          <span className="font-semibold">#{incident?.id || "N/A"}</span>
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={expandAllSections}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 rounded hover:bg-blue-50"
          >
            Expand All
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={collapseAllSections}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 rounded hover:bg-blue-50"
          >
            Collapse All
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit, scrollToTopOnError)} className="space-y-4">
        {/* Validation Summary */}
        {Object.keys(errors).length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <FiAlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-red-700">
                  Please complete the required fields:
                </h4>
                <ul className="mt-2 text-sm text-red-600 list-disc list-inside space-y-1">
                  {errors.is_reportable && (
                    <li>Select whether the incident is reportable</li>
                  )}
                  {errors.remedial_actions_other && (
                    <li>Specify other remedial action</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Section 1: Is the incident reportable? */}
        <CollapsibleSection
          title="Is the incident reportable?"
          icon={<FiCheckCircle className="w-4 h-4" />}
          isExpanded={expandedSections.reportable}
          onToggle={() => toggleSection("reportable")}
          required
        >
          <div className="flex gap-4">
            <label
              className={classNames(
                "flex-1 flex items-center justify-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all",
                watch("is_reportable") === "yes"
                  ? "border-red-500 bg-red-50"
                  : "border-gray-200 hover:border-gray-300",
                readOnly && "cursor-not-allowed",
              )}
            >
              <input
                type="radio"
                {...register("is_reportable", { required: true })}
                value="yes"
                className="w-5 h-5 text-red-600"
                disabled={readOnly}
              />
              <span
                className={
                  watch("is_reportable") === "yes"
                    ? "text-red-700 font-medium"
                    : "text-gray-700"
                }
              >
                Yes
              </span>
            </label>
            <label
              className={classNames(
                "flex-1 flex items-center justify-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all",
                watch("is_reportable") === "no"
                  ? "border-green-500 bg-green-50"
                  : "border-gray-200 hover:border-gray-300",
                readOnly && "cursor-not-allowed",
              )}
            >
              <input
                type="radio"
                {...register("is_reportable", { required: true })}
                value="no"
                className="w-5 h-5 text-green-600"
                disabled={readOnly}
              />
              <span
                className={
                  watch("is_reportable") === "no"
                    ? "text-green-700 font-medium"
                    : "text-gray-700"
                }
              >
                No
              </span>
            </label>
          </div>
          {errors.is_reportable && (
            <p className="text-red-500 text-sm mt-2">
              Please select whether the incident is reportable.
            </p>
          )}
        </CollapsibleSection>

        {/* Section 2: Interview Notes */}
        <CollapsibleSection
          title="Interview Notes"
          icon={<FiClipboard className="w-4 h-4" />}
          isExpanded={expandedSections.interviews}
          onToggle={() => toggleSection("interviews")}
          badgeText={`${participantInterviews.filter((i) => i.notes.trim()).length + staffInterviews.filter((i) => i.notes.trim()).length} notes`}
        >
          <div className="space-y-6">
            {/* Participant Interview Notes */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {participantLabel} interview notes?
                  </label>
                  <span className="text-xs text-gray-400">
                    ({participantInterviews.length}/{MAX_INTERVIEWS})
                  </span>
                </div>
                {!readOnly && participantInterviews.length < MAX_INTERVIEWS && (
                  <button
                    type="button"
                    onClick={addParticipantInterview}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                  >
                    <AiOutlinePlus className="w-3 h-3" />
                    Add another
                  </button>
                )}
                {!readOnly &&
                  participantInterviews.length >= MAX_INTERVIEWS && (
                    <span className="text-xs text-gray-400">
                      Max limit reached
                    </span>
                  )}
              </div>
              <div
                className={classNames(
                  "space-y-3",
                  participantInterviews.length > 3 &&
                    "max-h-[300px] overflow-y-auto pr-2",
                )}
              >
                {participantInterviews.map((interview, index) => (
                  <div key={index} className="relative">
                    <div className="absolute top-2 left-3 text-xs text-gray-400 font-medium">
                      #{index + 1}
                    </div>
                    <textarea
                      value={interview.notes}
                      onChange={(e) =>
                        updateParticipantInterview(index, e.target.value)
                      }
                      rows={3}
                      className="w-full px-4 py-3 pt-7 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm pr-10"
                      placeholder={`${participantLabel} interview ${index + 1}...`}
                      disabled={readOnly}
                    />
                    {!readOnly && participantInterviews.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeParticipantInterview(index)}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700 p-1"
                      >
                        <AiOutlineDelete className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Staff Interview Notes */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Staff members interview notes?
                  </label>
                  <span className="text-xs text-gray-400">
                    ({staffInterviews.length}/{MAX_INTERVIEWS})
                  </span>
                </div>
                {!readOnly && staffInterviews.length < MAX_INTERVIEWS && (
                  <button
                    type="button"
                    onClick={addStaffInterview}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                  >
                    <AiOutlinePlus className="w-3 h-3" />
                    Add another
                  </button>
                )}
                {!readOnly && staffInterviews.length >= MAX_INTERVIEWS && (
                  <span className="text-xs text-gray-400">
                    Max limit reached
                  </span>
                )}
              </div>
              <div
                className={classNames(
                  "space-y-3",
                  staffInterviews.length > 3 &&
                    "max-h-[300px] overflow-y-auto pr-2",
                )}
              >
                {staffInterviews.map((interview, index) => (
                  <div key={index} className="relative">
                    <div className="absolute top-2 left-3 text-xs text-gray-400 font-medium">
                      #{index + 1}
                    </div>
                    <textarea
                      value={interview.notes}
                      onChange={(e) =>
                        updateStaffInterview(index, e.target.value)
                      }
                      rows={3}
                      className="w-full px-4 py-3 pt-7 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm pr-10"
                      placeholder={`Staff interview ${index + 1}...`}
                      disabled={readOnly}
                    />
                    {!readOnly && staffInterviews.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeStaffInterview(index)}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700 p-1"
                      >
                        <AiOutlineDelete className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Section 3: Advocacy */}
        <CollapsibleSection
          title="Advocacy"
          icon={<FiUsers className="w-4 h-4" />}
          isExpanded={expandedSections.advocacy}
          onToggle={() => toggleSection("advocacy")}
          badgeText={
            advocacyOffered ? (advocacyOffered === "yes" ? "Yes" : "No") : ""
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Has Advocacy been offered to {participantLabel}?
              </label>
              <div className="flex gap-4">
                <label
                  className={classNames(
                    "flex-1 flex items-center justify-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all",
                    advocacyOffered === "no"
                      ? "border-red-500 bg-red-50"
                      : "border-gray-200 hover:border-gray-300",
                    readOnly && "cursor-not-allowed",
                  )}
                >
                  <input
                    type="radio"
                    {...register("advocacy_offered")}
                    value="no"
                    className="w-5 h-5 text-red-600"
                    disabled={readOnly}
                  />
                  <span
                    className={
                      advocacyOffered === "no"
                        ? "text-red-700 font-medium"
                        : "text-gray-700 font-medium"
                    }
                  >
                    No
                  </span>
                </label>
                <label
                  className={classNames(
                    "flex-1 flex items-center justify-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all",
                    advocacyOffered === "yes"
                      ? "border-green-500 bg-green-50"
                      : "border-gray-200 hover:border-gray-300",
                    readOnly && "cursor-not-allowed",
                  )}
                >
                  <input
                    type="radio"
                    {...register("advocacy_offered")}
                    value="yes"
                    className="w-5 h-5 text-green-600"
                    disabled={readOnly}
                  />
                  <span
                    className={
                      advocacyOffered === "yes"
                        ? "text-green-700 font-medium"
                        : "text-gray-700"
                    }
                  >
                    Yes
                  </span>
                </label>
              </div>
            </div>
            {advocacyOffered === "yes" && (
              <div className="animate-fadeIn">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Details
                </label>
                <input
                  type="text"
                  {...register("advocacy_contact_name")}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Enter advocacy contact name..."
                  disabled={readOnly}
                />
              </div>
            )}
            {advocacyOffered === "no" && (
              <div className="animate-fadeIn">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Details
                </label>
                <input
                  type="text"
                  {...register("advocacy_details")}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Enter details..."
                  disabled={readOnly}
                />
              </div>
            )}
          </div>
        </CollapsibleSection>

        {/* Section 4: Investigation Checks */}
        <CollapsibleSection
          title="Investigation Checks"
          icon={<HiOutlineClipboardCheck className="w-4 h-4" />}
          isExpanded={expandedSections.checks}
          onToggle={() => toggleSection("checks")}
          badgeText={`${INVESTIGATION_CHECKS.filter((c) => watch(c.key)).length}/${INVESTIGATION_CHECKS.length} completed`}
        >
          <p className="text-xs text-gray-500 mb-4">
            Complete the following checks as part of the investigation process
          </p>
          <div className="space-y-2">
            {INVESTIGATION_CHECKS.map((check) => (
              <div
                key={check.key}
                className="flex items-center justify-between py-2.5 px-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
              >
                <span className="text-sm text-gray-700 pr-4 flex-1">
                  {check.label}
                </span>
                <YesNoNAToggle
                  name={check.key}
                  value={watch(check.key)}
                  onChange={(val) => !readOnly && setValue(check.key, val)}
                  readOnly={readOnly}
                />
              </div>
            ))}
          </div>
        </CollapsibleSection>

        {/* Section 5: Root Cause Analysis */}
        <CollapsibleSection
          title="Root Cause Analysis"
          icon={<FiAlertTriangle className="w-4 h-4" />}
          isExpanded={expandedSections.rootCause}
          onToggle={() => toggleSection("rootCause")}
          badgeText={watch("root_cause_analysis") ? "Completed" : ""}
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              After reviewing the above prompts and undertaking interviews
              and/or site visits, what are the identified Root cause(s) of the
              incident?
            </label>
            <textarea
              {...register("root_cause_analysis")}
              rows={5}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Describe the root cause(s) of the incident..."
              disabled={readOnly}
            />
          </div>
        </CollapsibleSection>

        {/* Section 6: Remedial Actions */}
        <CollapsibleSection
          title="Remedial Actions Recommended"
          icon={<FiFileText className="w-4 h-4" />}
          isExpanded={expandedSections.remedial}
          onToggle={() => toggleSection("remedial")}
          badgeText={
            selectedRemedialActions.length > 0
              ? `${selectedRemedialActions.length} selected`
              : ""
          }
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {REMEDIAL_ACTIONS.map((action) => (
              <label
                key={action.value}
                className={classNames(
                  "flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-all text-sm",
                  selectedRemedialActions.includes(action.value)
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-gray-200 hover:border-gray-300 text-gray-700",
                  readOnly && "cursor-not-allowed opacity-70",
                )}
              >
                <input
                  type="checkbox"
                  {...register("remedial_actions")}
                  value={action.value}
                  className="w-4 h-4 text-blue-600 rounded"
                  disabled={readOnly}
                />
                <span>{action.label}</span>
              </label>
            ))}
          </div>
          {hasRemedialOthers && (
            <div className="mt-4 animate-fadeIn">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specify Other Remedial Action{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register("remedial_actions_other", {
                  required: hasRemedialOthers
                    ? "Please specify other remedial action"
                    : false,
                })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Enter other remedial action..."
                disabled={readOnly}
              />
              {errors.remedial_actions_other && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.remedial_actions_other.message}
                </p>
              )}
            </div>
          )}
        </CollapsibleSection>

        {/* Action Buttons - Sticky Footer */}
        {!readOnly && (
          <div className="sticky bottom-0 bg-white pt-4 pb-2 -mx-6 px-6 border-t border-gray-200 mt-6">
            <div className="flex justify-between items-center">
              <div className="flex gap-3">
                {onCancel && (
                  <CustomButton
                    variant="wsecondary"
                    label="Cancel"
                    clickHandler={onCancel}
                    isDisabled={isLoading}
                  />
                )}
                {onSaveDraft && (
                  <CustomButton
                    variant="wsecondary"
                    label="Save Draft"
                    Icon={<AiOutlineSave className="w-5 h-5" />}
                    clickHandler={handleDraftSave}
                    isDisabled={isLoading}
                  />
                )}
              </div>

              <CustomButton
                type="submit"
                variant="primary"
                label="Save Investigation"
                Icon={<AiOutlineCheckCircle className="w-5 h-5" />}
                isDisabled={isLoading}
                showLoading={isLoading}
              />
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

/**
 * ReviewCloseForm Component (Part 5)
 * Used by managers to review and close an incident after investigation
 *
 * Fields stored in close_review_data JSON:
 * - new_risks_identified (yes/no) + new_risks_details
 * - risk_prevention_required (yes/no) + risk_prevention_details
 * - risk_recurrence_rating (low/medium/high/nil)
 * - further_action_needed (yes/no) + further_action_details
 * - manager_review_comments
 * - final_status (reported/closed)
 */
export const ReviewCloseForm = ({
  incident,
  investigation,
  onSubmit,
  onSaveDraft,
  onCancel,
  isLoading = false,
  initialData = null,
  readOnly = false,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: initialData
      ? {
          ...initialData,
          ndis_reference_number:
            initialData.ndis_reference_number ?? incident?.ndis_reference_number ?? "",
        }
      : {
          new_risks_identified: "",
          new_risks_details: "",
          risk_prevention_required: "",
          risk_prevention_details: "",
          risk_recurrence_rating: "",
          further_action_needed: "",
          further_action_details: "",
          manager_review_comments: "",
          final_status: "",
          reported_to_commission_at: "",
          ndis_reference_number: incident?.ndis_reference_number ?? "",
        },
  });

  const [reportedDate, setReportedDate] = useState(
    initialData?.reported_to_commission_at || null,
  );

  // State for collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    summary: true,
    riskAssessment: true,
    furtherAction: false,
    managerReview: false,
    finalStatus: true,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const expandAllSections = () => {
    setExpandedSections({
      summary: true,
      riskAssessment: true,
      furtherAction: true,
      managerReview: true,
      finalStatus: true,
    });
  };

  const collapseAllSections = () => {
    setExpandedSections({
      summary: false,
      riskAssessment: false,
      furtherAction: false,
      managerReview: false,
      finalStatus: false,
    });
  };

  // Watch for conditional fields
  const newRisksIdentified = watch("new_risks_identified");
  const riskPreventionRequired = watch("risk_prevention_required");
  const furtherActionNeeded = watch("further_action_needed");
  const finalStatus = watch("final_status");

  const formContainerRef = useRef(null);

  const scrollToTopOnError = () => {
    requestAnimationFrame(() => {
      let el = formContainerRef.current;
      while (el) {
        const style = getComputedStyle(el);
        if (
          el.scrollHeight > el.clientHeight &&
          ["auto", "scroll"].includes(style.overflowY)
        ) {
          el.scrollTo({ top: 0, behavior: "smooth" });
          break;
        }
        el = el.parentElement;
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const handleFormSubmit = (data) => {
    if (finalStatus === "reported" && !reportedDate) {
      setError("reported_to_commission_at", {
        type: "required",
        message: "Date Reported to Commission is required when status is Reported",
      });
      scrollToTopOnError();
      return;
    }
    clearErrors("reported_to_commission_at");
    onSubmit({
      ...data,
      reported_to_commission_at:
        finalStatus === "reported" ? reportedDate : null,
      ndis_reference_number: data.ndis_reference_number || null,
    });
  };

  const handleDraftSave = () => {
    const data = watch();
    onSaveDraft?.({
      ...data,
      reported_to_commission_at: reportedDate || null,
      ndis_reference_number: data.ndis_reference_number || null,
    });
  };

  const riskRatingOptions = [
    { value: "nil", label: "Nil" },
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
  ];

  const missingReportedDate =
    finalStatus === "reported" && !reportedDate;
  const hasValidationErrors =
    !finalStatus ||
    missingReportedDate ||
    Object.keys(errors).length > 0;

  return (
    <div ref={formContainerRef} className="p-6">
      {/* Subheader with expand/collapse controls */}
      <div className="mb-6 pb-4 border-b border-gray-200 flex items-center justify-between">
        <p className="text-gray-600 text-sm">
          Complete the review for Incident{" "}
          <span className="font-semibold">#{incident?.id || "N/A"}</span>
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={expandAllSections}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 rounded hover:bg-blue-50"
          >
            Expand All
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={collapseAllSections}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 rounded hover:bg-blue-50"
          >
            Collapse All
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit, scrollToTopOnError)} className="space-y-4">
        {/* Validation Summary */}
        {Object.keys(errors).length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <FiAlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-red-700">
                  Please complete the required fields:
                </h4>
                <ul className="mt-2 text-sm text-red-600 list-disc list-inside space-y-1">
                  {errors.risk_recurrence_rating && (
                    <li>Select the risk recurrence rating</li>
                  )}
                  {errors.final_status && (
                    <li>Select the final status (Reported or Closed)</li>
                  )}
                  {errors.reported_to_commission_at && (
                    <li>Date Reported to Commission is required when status is Reported</li>
                  )}
                  {errors.ndis_reference_number && (
                    <li>{errors.ndis_reference_number.message}</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Section 1: Investigation Summary - Read Only */}
        {investigation?.data && (
          <CollapsibleSection
            title="Investigation Summary"
            icon={<FiSearch className="w-4 h-4" />}
            isExpanded={expandedSections.summary}
            onToggle={() => toggleSection("summary")}
            badgeText={
              investigation.data.is_reportable === "yes"
                ? "Reportable"
                : "Non-Reportable"
            }
          >
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Started:</span>
                <span className="ml-2 font-medium">
                  {investigation.started_at
                    ? moment(investigation.started_at).format("DD MMM YYYY")
                    : "Not specified"}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Reportable:</span>
                <span
                  className={classNames(
                    "ml-2 px-2 py-0.5 rounded-full text-xs font-medium",
                    investigation.data.is_reportable === "yes"
                      ? "bg-red-100 text-red-700"
                      : "bg-green-100 text-green-700",
                  )}
                >
                  {investigation.data.is_reportable === "yes"
                    ? "Yes - Reportable"
                    : "No - Not Reportable"}
                </span>
              </div>
              {investigation.data.root_cause_analysis && (
                <div className="col-span-2">
                  <span className="text-gray-500">Root Cause:</span>
                  <p className="mt-1 text-gray-700">
                    {investigation.data.root_cause_analysis}
                  </p>
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Section 2: Risk Assessment */}
        <CollapsibleSection
          title="Risk Assessment"
          icon={<FiAlertTriangle className="w-4 h-4" />}
          isExpanded={expandedSections.riskAssessment}
          onToggle={() => toggleSection("riskAssessment")}
          required
          badgeText={
            watch("risk_recurrence_rating")
              ? `Risk: ${watch("risk_recurrence_rating").charAt(0).toUpperCase() + watch("risk_recurrence_rating").slice(1)}`
              : ""
          }
        >
          <div className="space-y-4">
            {/* New Risks Identified */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">
                  Are there any new risks identified?
                  <span className="block text-xs text-gray-500 mt-0.5">
                    (If yes, add them to risk register and continuous
                    improvement plan)
                  </span>
                </label>
                <YesNoNAToggle
                  name="new_risks_identified"
                  value={newRisksIdentified}
                  onChange={(val) =>
                    !readOnly && setValue("new_risks_identified", val)
                  }
                  readOnly={readOnly}
                />
              </div>
              {newRisksIdentified === "yes" && (
                <textarea
                  {...register("new_risks_details")}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Describe the new risks identified..."
                  disabled={readOnly}
                />
              )}
            </div>

            {/* Risk Prevention */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">
                  Are any risk prevention or mitigation strategies required?
                </label>
                <YesNoNAToggle
                  name="risk_prevention_required"
                  value={riskPreventionRequired}
                  onChange={(val) =>
                    !readOnly && setValue("risk_prevention_required", val)
                  }
                  readOnly={readOnly}
                />
              </div>
              {riskPreventionRequired === "yes" && (
                <textarea
                  {...register("risk_prevention_details")}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Describe the prevention/mitigation strategies..."
                  disabled={readOnly}
                />
              )}
            </div>

            {/* Risk Recurrence Rating */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Rate the risk for recurrence{" "}
                <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3">
                {riskRatingOptions.map((option) => (
                  <label
                    key={option.value}
                    className={classNames(
                      "flex-1 flex items-center justify-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all",
                      watch("risk_recurrence_rating") === option.value
                        ? option.value === "high"
                          ? "border-red-500 bg-red-50"
                          : option.value === "medium"
                            ? "border-yellow-500 bg-yellow-50"
                            : option.value === "low"
                              ? "border-blue-500 bg-blue-50"
                              : "border-green-500 bg-green-50"
                        : "border-gray-200 hover:border-gray-300",
                      readOnly && "cursor-not-allowed",
                    )}
                  >
                    <input
                      type="radio"
                      {...register("risk_recurrence_rating", {
                        required: true,
                      })}
                      value={option.value}
                      className="hidden"
                      disabled={readOnly}
                    />
                    <span className="font-medium">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Section 3: Further Action */}
        <CollapsibleSection
          title="Further Action"
          icon={<FiFileText className="w-4 h-4" />}
          isExpanded={expandedSections.furtherAction}
          onToggle={() => toggleSection("furtherAction")}
          badgeText={
            furtherActionNeeded
              ? furtherActionNeeded === "yes"
                ? "Action Required"
                : "No Action"
              : ""
          }
        >
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">
                Is further action needed?
                <span className="block text-xs text-gray-500 mt-0.5">
                  (If yes, specify the actions and timeline)
                </span>
              </label>
              <YesNoNAToggle
                name="further_action_needed"
                value={furtherActionNeeded}
                onChange={(val) =>
                  !readOnly && setValue("further_action_needed", val)
                }
                readOnly={readOnly}
              />
            </div>
            {furtherActionNeeded === "yes" && (
              <textarea
                {...register("further_action_details")}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Specify the actions and timeline..."
                disabled={readOnly}
              />
            )}
          </div>
        </CollapsibleSection>

        {/* Section 4: Manager Review */}
        <CollapsibleSection
          title="Manager's Review"
          icon={<FiUsers className="w-4 h-4" />}
          isExpanded={expandedSections.managerReview}
          onToggle={() => toggleSection("managerReview")}
          badgeText={watch("manager_review_comments") ? "Completed" : ""}
        >
          <textarea
            {...register("manager_review_comments")}
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="Enter manager review comments..."
            disabled={readOnly}
          />
        </CollapsibleSection>

        {/* Section 5: Final Status */}
        <CollapsibleSection
          title="Final Status"
          icon={<FiCheckCircle className="w-4 h-4" />}
          isExpanded={expandedSections.finalStatus}
          onToggle={() => toggleSection("finalStatus")}
          required
          allowOverflow={true}
          badgeText={
            finalStatus
              ? finalStatus === "reported"
                ? "Reported"
                : "Closed"
              : ""
          }
        >
          <p className="text-xs text-gray-500 mb-4">
            Has the incident been reported to the commission or marked as
            closed?
          </p>
          <div className="flex gap-4">
            <label
              className={classNames(
                "flex-1 flex items-center justify-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all",
                finalStatus === "reported"
                  ? "border-purple-500 bg-purple-50"
                  : "border-gray-200 hover:border-gray-300",
                readOnly && "cursor-not-allowed",
              )}
            >
              <input
                type="radio"
                {...register("final_status", { required: true })}
                value="reported"
                className="w-5 h-5 text-purple-600"
                disabled={readOnly}
              />
              <div>
                <span
                  className={classNames(
                    "font-medium",
                    finalStatus === "reported"
                      ? "text-purple-700"
                      : "text-gray-700",
                  )}
                >
                  Reported
                </span>
                <p className="text-xs text-gray-500">
                  Report to NDIS Commission
                </p>
              </div>
            </label>
            <label
              className={classNames(
                "flex-1 flex items-center justify-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all",
                finalStatus === "closed"
                  ? "border-green-500 bg-green-50"
                  : "border-gray-200 hover:border-gray-300",
                readOnly && "cursor-not-allowed",
              )}
            >
              <input
                type="radio"
                {...register("final_status", { required: true })}
                value="closed"
                className="w-5 h-5 text-green-600"
                disabled={readOnly}
              />
              <div>
                <span
                  className={classNames(
                    "font-medium",
                    finalStatus === "closed"
                      ? "text-green-700"
                      : "text-gray-700",
                  )}
                >
                  Closed
                </span>
                <p className="text-xs text-gray-500">Close without reporting</p>
              </div>
            </label>
          </div>
          {errors.final_status && (
            <p className="text-red-500 text-sm mt-2">
              Please select Reported or Closed.
            </p>
          )}

          {/* Reported Date (only if reported) */}
          {finalStatus === "reported" && (
            <div className="mt-4 animate-fadeIn">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Reported to Commission{" "}
                <span className="text-red-500">*</span>
              </label>
              {(errors.reported_to_commission_at || missingReportedDate) && (
                <p className="text-red-500 text-xs mb-1">
                  {errors.reported_to_commission_at?.message ||
                    "Date Reported to Commission is required when status is Reported"}
                </p>
              )}
              <div
                className={classNames(
                  "relative rounded-lg",
                  (errors.reported_to_commission_at || missingReportedDate) &&
                    "ring-2 ring-red-300 ring-offset-1 rounded-lg",
                )}
              >
                <DateRangePickerNew
                  isSingleDate
                  singleSelectedDate={
                    reportedDate ? new Date(reportedDate) : null
                  }
                  handleChange={(date) => {
                    setReportedDate(moment(date).format("YYYY-MM-DD"));
                    clearErrors("reported_to_commission_at");
                  }}
                  handleCancel={() => setReportedDate(null)}
                  maxDate={new Date()}
                  placeholder="Select date"
                  isDisabled={readOnly}
                  showCalendarIcon={true}
                />
              </div>
            </div>
          )}

          {/* NDIS Commission Reference Number (only when Reported) */}
          {finalStatus === "reported" && (
            <div className="mt-4 animate-fadeIn">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                NDIS Commission Reference Number
              </label>
              <input
                type="text"
                maxLength={25}
                {...register("ndis_reference_number", {
                  maxLength: {
                    value: 25,
                    message: "Maximum 25 characters",
                  },
                })}
                value={watch("ndis_reference_number") ?? ""}
                onChange={(e) => {
                  setValue("ndis_reference_number", e.target.value.slice(0, 25), {
                    shouldValidate: true,
                  });
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Enter NDIS Commission Reference Number"
                disabled={readOnly}
              />
              <p className="text-xs text-gray-500 mt-1">
                Max 25 characters (letters, numbers, and special characters allowed)
              </p>
              {errors.ndis_reference_number && (
                <p className="text-red-500 text-xs mt-2">
                  {errors.ndis_reference_number.message}
                </p>
              )}
            </div>
          )}
        </CollapsibleSection>

        {/* Action Buttons - Sticky Footer */}
        {!readOnly && (
          <div className="sticky bottom-0 bg-white pt-4 pb-2 -mx-6 px-6 border-t border-gray-200 mt-6">
            <div className="flex justify-between items-center">
              <div className="flex gap-3">
                <CustomButton
                  variant="wsecondary"
                  label="Cancel"
                  clickHandler={onCancel}
                  isDisabled={isLoading}
                />
                {onSaveDraft && (
                  <CustomButton
                    variant="wsecondary"
                    label="Save & Exit"
                    Icon={<AiOutlineSave className="w-5 h-5" />}
                    clickHandler={handleDraftSave}
                    isDisabled={isLoading}
                  />
                )}
              </div>

              <CustomButton
                type="submit"
                variant={finalStatus === "reported" ? "primary" : "btn-green"}
                label={
                  finalStatus === "reported"
                    ? "Report & Close"
                    : "Close Incident"
                }
                Icon={<AiOutlineCheckCircle className="w-5 h-5" />}
                isDisabled={isLoading || hasValidationErrors}
                showLoading={isLoading}
                title={
                  hasValidationErrors
                    ? "Complete required fields to continue"
                    : undefined
                }
                cssClass={
                  hasValidationErrors
                    ? "opacity-60 cursor-not-allowed"
                    : undefined
                }
              />
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default {
  InvestigationForm,
  ReviewCloseForm,
};
